import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Input from "@/components/base/Input";
import Button from "@/components/base/Button";
import { interventionService, estudianteService, caseService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";

interface MedidaRecord {
  id: string;
  casoId: string;
  estudianteNombre: string;
  estudianteCurso: string;
  tipoMedida: string; // e.g. "Formativa / Servicio Comunitario"
  fechaInicio: string;
  fechaTermino: string;
  estadoBadge: string; // e.g. "Badge:activo:En Ejecución"
  descripcion: string;
}

export default function ConvivenciaMedidasPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todos");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [medidas, setMedidas] = useState<MedidaRecord[]>([]);

  // Selection data
  const [students, setStudents] = useState<any[]>([]);
  const [allCases, setAllCases] = useState<any[]>([]);

  // Modals visibility states
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState<MedidaRecord | null>(null);
  const [editingRecord, setEditingRecord] = useState<MedidaRecord | null>(null);

  // Form states
  const [formEstudianteId, setFormEstudianteId] = useState("");
  const [formCasoId, setFormCasoId] = useState("");
  const [formTipo, setFormTipo] = useState("Formativa / Servicio Comunitario");
  const [formFechaInicio, setFormFechaInicio] = useState(new Date().toISOString().split("T")[0]);
  const [formFechaTermino, setFormFechaTermino] = useState("");
  const [formEstado, setFormEstado] = useState("activo:En Ejecución");
  const [formDescripcion, setFormDescripcion] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [intsData, studentsData, casesData] = await Promise.all([
        interventionService.list(),
        estudianteService.list().catch(() => []),
        caseService.list().catch(() => [])
      ]);

      if (Array.isArray(studentsData)) setStudents(studentsData);
      if (Array.isArray(casesData)) setAllCases(casesData.map(mapDbCaseToFrontend));

      if (Array.isArray(intsData)) {
        // Filter measures/agreements
        const measures = intsData
          .filter((i: any) =>
            ["medida_formativa", "medida_disciplinaria", "acuerdo_reparatorio", "acuerdo_convivencia"].includes(i.tipo)
          )
          .map((i: any) => {
            const rawTermino = i.proximos_pasos || "";
            const splitTermino = rawTermino.split("|");
            const fechaTermino = splitTermino[0] || "Indefinido";
            const estadoBadge = splitTermino[1] ? "Badge:" + splitTermino[1] : "Badge:activo:En Ejecución";

            return {
              id: i.id,
              casoId: i.caso_id,
              estudianteNombre: i.estudiante_nombre || "Estudiante",
              estudianteCurso: i.estudiante_curso || "Curso",
              tipoMedida: i.acuerdos || "Medida Formativa",
              fechaInicio: i.fecha ? new Date(i.fecha).toISOString().split("T")[0] : "",
              fechaTermino: fechaTermino,
              estadoBadge: estadoBadge,
              descripcion: i.descripcion || ""
            };
          });
        setMedidas(measures);
      }
    } catch (err: any) {
      console.error("Error loading measures:", err);
      setError("Error al cargar medidas formativas: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleExport = () => {
    const csvHeaders = ["Estudiante", "Curso", "Tipo de Medida", "Fecha Inicio", "Fecha Término", "Estado"];
    const rows = filtered.map(r => [
      r.estudianteNombre,
      r.estudianteCurso,
      r.tipoMedida,
      r.fechaInicio,
      r.fechaTermino,
      r.estadoBadge.replace(/^Badge:[^:]+:/, "")
    ]);
    const csvContent = "\uFEFF" + [csvHeaders.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Medidas_y_Acuerdos_Formativos_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const handleOpenAdd = () => {
    setFormEstudianteId("");
    setFormCasoId("");
    setFormTipo("Formativa / Servicio Comunitario");
    setFormFechaInicio(new Date().toISOString().split("T")[0]);
    setFormFechaTermino("");
    setFormEstado("activo:En Ejecución");
    setFormDescripcion("");
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formCasoId) {
      alert("Por favor seleccione un caso activo asociado.");
      return;
    }

    try {
      setLoading(true);
      // Map subtype
      let dbTipo = "medida_formativa";
      if (formTipo.includes("Disciplinaria")) dbTipo = "medida_disciplinaria";
      else if (formTipo.includes("Reparatorio")) dbTipo = "acuerdo_reparatorio";
      else if (formTipo.includes("Aula") || formTipo.includes("Convivencia")) dbTipo = "acuerdo_convivencia";

      await interventionService.create({
        caso_id: formCasoId,
        tipo: dbTipo,
        descripcion: formDescripcion || `Aplicación de medida: ${formTipo}`,
        fecha: formFechaInicio,
        acuerdos: formTipo,
        proximos_pasos: `${formFechaTermino || "Indefinido"}|${formEstado}`,
        confidencial: false
      });

      setShowAddModal(false);
      await loadData();
    } catch (err: any) {
      console.error("Error creating measure:", err);
      alert("Error al registrar medida: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (rec: MedidaRecord) => {
    const rawState = rec.estadoBadge.replace("Badge:", "");
    setFormTipo(rec.tipoMedida);
    setFormFechaInicio(rec.fechaInicio);
    setFormFechaTermino(rec.fechaTermino === "Indefinido" ? "" : rec.fechaTermino);
    setFormEstado(rawState);
    setFormDescripcion(rec.descripcion);
    setEditingRecord(rec);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRecord) return;

    try {
      setLoading(true);
      let dbTipo = "medida_formativa";
      if (formTipo.includes("Disciplinaria")) dbTipo = "medida_disciplinaria";
      else if (formTipo.includes("Reparatorio")) dbTipo = "acuerdo_reparatorio";
      else if (formTipo.includes("Aula") || formTipo.includes("Convivencia")) dbTipo = "acuerdo_convivencia";

      await interventionService.update(editingRecord.id, {
        tipo: dbTipo,
        descripcion: formDescripcion,
        fecha: formFechaInicio,
        acuerdos: formTipo,
        proximos_pasos: `${formFechaTermino || "Indefinido"}|${formEstado}`,
        participantes: [],
        confidencial: false
      });

      setEditingRecord(null);
      await loadData();
    } catch (err: any) {
      console.error("Error updating measure:", err);
      alert("Error al actualizar medida: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("¿Estás seguro de eliminar este registro?")) {
      try {
        setLoading(true);
        await interventionService.delete(id);
        await loadData();
      } catch (err: any) {
        console.error("Error deleting measure:", err);
        alert("Error al eliminar medida: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const filtered = useMemo(() => {
    return medidas.filter(row => {
      const textMatch = [row.estudianteNombre, row.estudianteCurso, row.tipoMedida, row.fechaInicio, row.fechaTermino]
        .some(cell => String(cell).toLowerCase().includes(search.toLowerCase()));
      if (!textMatch) return false;
      
      if (filter === "todos") return true;
      if (filter === "critico") return row.estadoBadge.toLowerCase().includes("vencido") || row.estadoBadge.toLowerCase().includes("incumplida");
      
      const badgeState = row.estadoBadge.split(":")[1] || "";
      return badgeState.toLowerCase() === filter.toLowerCase();
    });
  }, [medidas, search, filter]);

  const getStatusVariant = (statusText: string) => {
    const st = statusText.toLowerCase();
    if (st.includes("activo") || st.includes("completado") || st.includes("cumplida") || st.includes("ejecución") || st.includes("abierto")) return "primary";
    if (st.includes("pendiente") || st.includes("evaluacion") || st.includes("revision")) return "warning";
    if (st.includes("vencido") || st.includes("critico") || st.includes("urgente") || st.includes("incumplida")) return "danger";
    return "neutral";
  };

  const activeCasesForSelectedStudent = useMemo(() => {
    if (!formEstudianteId) return [];
    return allCases.filter(c => String(c.studentId) === String(formEstudianteId) && c.estado !== "cerrado");
  }, [formEstudianteId, allCases]);

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Medidas y Acuerdos Formativos</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Registro y seguimiento de medidas disciplinarias formativas y acuerdos reparatorios aplicados a estudiantes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleExport} className="cursor-pointer">
            <i className="ri-download-line mr-1.5" /> Exportar
          </Button>
          <Button size="sm" onClick={handleOpenAdd} className="cursor-pointer bg-primary-500 text-white">
            <i className="ri-add-line mr-1.5" /> Agregar Registro
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-sm"
          fullWidth={false}
        />
        <div className="flex gap-2 overflow-x-auto">
          {["todos", "activo", "pendiente", "critico"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer capitalize transition-all ${
                filter === f
                  ? "bg-foreground-900 text-white border-foreground-900"
                  : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
              }`}
            >
              {f === "todos" ? "Todos" : f === "critico" ? "⚠️ Críticos" : f}
            </button>
          ))}
        </div>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-foreground-500">
              <i className="ri-loader-4-line animate-spin mr-2" /> Cargando registros...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Estudiante</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Curso</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Tipo de Medida</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Fecha Inicio</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Fecha Término</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Estado</th>
                  <th className="text-right py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const stateVal = row.estadoBadge.replace("Badge:", "").split(":")[1] || "En Ejecución";
                  return (
                    <tr key={row.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="py-3.5 px-4 font-semibold text-foreground-900 whitespace-nowrap">{row.estudianteNombre}</td>
                      <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">{row.estudianteCurso}</td>
                      <td className="py-3.5 px-4 text-foreground-700 font-medium text-xs">{row.tipoMedida}</td>
                      <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">{row.fechaInicio}</td>
                      <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">{row.fechaTermino}</td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <Badge variant={getStatusVariant(row.estadoBadge)}>{stateVal}</Badge>
                      </td>
                      <td className="py-3.5 px-4 text-right whitespace-nowrap space-x-1.5">
                        <button 
                          onClick={() => setViewingRecord(row)}
                          className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1"
                          title="Ver detalle"
                        >
                          <i className="ri-eye-line text-base" />
                        </button>
                        <button 
                          onClick={() => handleOpenEdit(row)}
                          className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-base" />
                        </button>
                        <button 
                          onClick={() => handleDelete(row.id)}
                          className="text-foreground-400 hover:text-red-600 transition-colors cursor-pointer p-1"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-foreground-400">
                      No se encontraron registros que coincidan con la búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* ── MODAL: AGREGAR REGISTRO ── */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={() => setShowAddModal(false)}>
          <form onSubmit={handleAddSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Registrar Medida / Acuerdo</h2>
              <button type="button" onClick={() => setShowAddModal(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estudiante</label>
                <select
                  value={formEstudianteId}
                  onChange={(e) => { setFormEstudianteId(e.target.value); setFormCasoId(""); }}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                >
                  <option value="">Seleccione estudiante...</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre} - {s.curso}</option>
                  ))}
                </select>
              </div>

              {formEstudianteId && (
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Caso Asociado Activo</label>
                  <select
                    value={formCasoId}
                    onChange={(e) => setFormCasoId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                    required
                  >
                    <option value="">Seleccione un caso...</option>
                    {activeCasesForSelectedStudent.map((c) => (
                      <option key={c.id} value={c.id}>{c.folio} - {c.titulo}</option>
                    ))}
                  </select>
                  {activeCasesForSelectedStudent.length === 0 && (
                    <p className="text-red-500 text-[10px] mt-1">El estudiante seleccionado no tiene casos de convivencia u orientación activos.</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tipo de Medida</label>
                <select
                  value={formTipo}
                  onChange={(e) => setFormTipo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="Formativa / Servicio Comunitario">Formativa / Servicio Comunitario</option>
                  <option value="Acuerdo Reparatorio / Disculpa Pública">Acuerdo Reparatorio / Disculpa Pública</option>
                  <option value="Disciplinaria / Suspensión Temporal">Disciplinaria / Suspensión Temporal</option>
                  <option value="Acuerdo de Convivencia de Aula">Acuerdo de Convivencia de Aula</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fecha Inicio</label>
                  <input
                    type="date"
                    value={formFechaInicio}
                    onChange={(e) => setFormFechaInicio(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fecha Término</label>
                  <input
                    type="date"
                    value={formFechaTermino}
                    onChange={(e) => setFormFechaTermino(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estado</label>
                <select
                  value={formEstado}
                  onChange={(e) => setFormEstado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="activo:En Ejecución">En Ejecución</option>
                  <option value="completado:Cumplida">Cumplida</option>
                  <option value="pendiente:Pendiente">Pendiente</option>
                  <option value="vencido:Incumplida">Incumplida</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Descripción / Bitácora</label>
                <textarea
                  value={formDescripcion}
                  onChange={(e) => setFormDescripcion(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  placeholder="Detalles sobre la medida..."
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={!formCasoId} className="flex-1 bg-primary-500 hover:bg-primary-600 text-white cursor-pointer">
                Registrar Medida
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* ── MODAL: EDITAR REGISTRO ── */}
      {editingRecord && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={() => setEditingRecord(null)}>
          <form onSubmit={handleEditSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Editar Medida: {editingRecord.estudianteNombre}</h2>
              <button type="button" onClick={() => setEditingRecord(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tipo de Medida</label>
                <select
                  value={formTipo}
                  onChange={(e) => setFormTipo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="Formativa / Servicio Comunitario">Formativa / Servicio Comunitario</option>
                  <option value="Acuerdo Reparatorio / Disculpa Pública">Acuerdo Reparatorio / Disculpa Pública</option>
                  <option value="Disciplinaria / Suspensión Temporal">Disciplinaria / Suspensión Temporal</option>
                  <option value="Acuerdo de Convivencia de Aula">Acuerdo de Convivencia de Aula</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fecha Inicio</label>
                  <input
                    type="date"
                    value={formFechaInicio}
                    onChange={(e) => setFormFechaInicio(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fecha Término</label>
                  <input
                    type="date"
                    value={formFechaTermino}
                    onChange={(e) => setFormFechaTermino(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estado</label>
                <select
                  value={formEstado}
                  onChange={(e) => setFormEstado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="activo:En Ejecución">En Ejecución</option>
                  <option value="completado:Cumplida">Cumplida</option>
                  <option value="pendiente:Pendiente">Pendiente</option>
                  <option value="vencido:Incumplida">Incumplida</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Descripción / Bitácora</label>
                <textarea
                  value={formDescripcion}
                  onChange={(e) => setFormDescripcion(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-primary-500 hover:bg-primary-600 text-white cursor-pointer">
                Guardar Cambios
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditingRecord(null)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* ── MODAL: VER REGISTRO ── */}
      {viewingRecord && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={() => setViewingRecord(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Detalle de Registro</h2>
              <button type="button" onClick={() => setViewingRecord(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              <p className="text-foreground-500"><strong className="text-foreground-700 text-sm">Estudiante:</strong> {viewingRecord.estudianteNombre}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Curso:</strong> {viewingRecord.estudianteCurso}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Tipo de Medida:</strong> {viewingRecord.tipoMedida}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Fecha Inicio:</strong> {viewingRecord.fechaInicio}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Fecha Término:</strong> {viewingRecord.fechaTermino}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Estado:</strong> <Badge variant={getStatusVariant(viewingRecord.estadoBadge)}>{viewingRecord.estadoBadge.replace("Badge:", "").split(":")[1]}</Badge></p>
              <div className="border-t border-background-150 pt-2 mt-2">
                <strong className="text-foreground-700 block mb-1">Descripción:</strong>
                <p className="text-foreground-600 bg-background-50 p-2 rounded border border-background-150/60 leading-relaxed font-sans">{viewingRecord.descripcion}</p>
              </div>
            </div>
            <div className="pt-2">
              <Button type="button" onClick={() => setViewingRecord(null)} className="w-full bg-primary-500 text-white cursor-pointer">
                Cerrar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
